/**
 * Safe recursive-descent formula parser and evaluator for calculated merge fields.
 * Supports: numbers, +, -, *, /, parentheses, and [Group.Token] field references.
 * No eval() used.
 */

const TOKEN_TYPES = {
  NUMBER: "NUMBER",
  OPERATOR: "OPERATOR",
  LPAREN: "LPAREN",
  RPAREN: "RPAREN",
  FIELD_REF: "FIELD_REF"
};

/**
 * Tokenizes a formula string into an array of tokens.
 * Field references must be in bracket format: [Document.SubTotal]
 */
export function tokenize(formula) {
  const tokens = [];
  let i = 0;
  const str = formula.trim();

  while (i < str.length) {
    const ch = str[i];

    if (/\s/.test(ch)) {
      // Skip whitespace
      i++;
    } else if (ch === "[") {
      // Field reference: [Group.Token]
      const end = str.indexOf("]", i);
      if (end === -1) {
        throw new Error(`Unclosed bracket at position ${i}`);
      }
      const ref = str.substring(i + 1, end);
      if (!ref.includes(".")) {
        throw new Error(`Invalid field reference: [${ref}]. Expected format: [Group.Field]`);
      }
      tokens.push({ type: TOKEN_TYPES.FIELD_REF, value: ref });
      i = end + 1;
    } else if (/[0-9.]/.test(ch)) {
      // Number (including decimals)
      let num = "";
      let hasDot = false;
      while (i < str.length && /[0-9.]/.test(str[i])) {
        if (str[i] === ".") {
          if (hasDot) throw new Error(`Invalid number at position ${i}`);
          hasDot = true;
        }
        num += str[i];
        i++;
      }
      tokens.push({ type: TOKEN_TYPES.NUMBER, value: parseFloat(num) });
    } else if ("+-*/%".includes(ch)) {
      // Operators
      tokens.push({ type: TOKEN_TYPES.OPERATOR, value: ch });
      i++;
    } else if (ch === "(") {
      tokens.push({ type: TOKEN_TYPES.LPAREN });
      i++;
    } else if (ch === ")") {
      tokens.push({ type: TOKEN_TYPES.RPAREN });
      i++;
    } else {
      throw new Error(`Unexpected character '${ch}' at position ${i}`);
    }
  }

  return tokens;
}

/**
 * Recursive descent parser. Builds an AST from tokens.
 * Precedence: parentheses > *, / > +, -
 */
export function parse(tokens) {
  let pos = 0;

  function peek() {
    return pos < tokens.length ? tokens[pos] : null;
  }

  function consume() {
    return tokens[pos++];
  }

  // expression = term (('+' | '-') term)*
  function parseExpression() {
    let left = parseTerm();

    while (peek() && peek().type === TOKEN_TYPES.OPERATOR && (peek().value === "+" || peek().value === "-")) {
      const op = consume().value;
      const right = parseTerm();
      left = { type: "BinaryOp", op, left, right };
    }

    return left;
  }

  // term = factor (('*' | '/') factor)*
  function parseTerm() {
    let left = parseFactor();

    while (peek() && peek().type === TOKEN_TYPES.OPERATOR && (peek().value === "*" || peek().value === "/" || peek().value === "%")) {
      const op = consume().value;
      const right = parseFactor();
      left = { type: "BinaryOp", op, left, right };
    }

    return left;
  }

  // factor = NUMBER | FIELD_REF | '(' expression ')' | unary(+/-) factor
  function parseFactor() {
    const token = peek();

    if (!token) {
      throw new Error("Unexpected end of formula");
    }

    // Unary + or -
    if (token.type === TOKEN_TYPES.OPERATOR && (token.value === "+" || token.value === "-")) {
      consume();
      const operand = parseFactor();
      if (token.value === "-") {
        return { type: "UnaryOp", op: "-", operand };
      }
      return operand;
    }

    if (token.type === TOKEN_TYPES.NUMBER) {
      consume();
      return { type: "Number", value: token.value };
    }

    if (token.type === TOKEN_TYPES.FIELD_REF) {
      consume();
      return { type: "FieldRef", value: token.value };
    }

    if (token.type === TOKEN_TYPES.LPAREN) {
      consume();
      const expr = parseExpression();
      const closing = peek();
      if (!closing || closing.type !== TOKEN_TYPES.RPAREN) {
        throw new Error("Missing closing parenthesis");
      }
      consume();
      return expr;
    }

    throw new Error(`Unexpected token: ${JSON.stringify(token)}`);
  }

  const ast = parseExpression();

  if (pos < tokens.length) {
    throw new Error(`Unexpected token after end of expression: ${JSON.stringify(tokens[pos])}`);
  }

  return ast;
}

/**
 * Evaluates an AST node given a values map.
 * valuesMap keys are without brackets: { "Document.SubTotal": 1234.56 }
 */
export function evaluate(node, valuesMap) {
  switch (node.type) {
    case "Number":
      return node.value;

    case "FieldRef": {
      const val = valuesMap[node.value];
      if (val === undefined || val === null || val === "") {
        return 0;
      }
      const num = typeof val === "number" ? val : parseFloat(val);
      return isNaN(num) ? 0 : num;
    }

    case "UnaryOp":
      return -evaluate(node.operand, valuesMap);

    case "BinaryOp": {
      const left = evaluate(node.left, valuesMap);
      const right = evaluate(node.right, valuesMap);

      switch (node.op) {
        case "+":
          return left + right;
        case "-":
          return left - right;
        case "*":
          return left * right;
        case "/":
          return right === 0 ? 0 : left / right;
        case "%":
          return right === 0 ? 0 : left % right;
        default:
          return 0;
      }
    }

    default:
      return 0;
  }
}

/**
 * Convenience wrapper: evaluates a formula string against a values map.
 */
export function evaluateFormula(formula, valuesMap) {
  const tokens = tokenize(formula);
  const ast = parse(tokens);
  return evaluate(ast, valuesMap);
}

/**
 * Extracts all field references from a formula string.
 * Returns array of references in bracket format: ["[Document.SubTotal]", "[Document.Shipping]"]
 */
export function extractFieldReferences(formula) {
  const regex = /\[([^\]]+)\]/g;
  const refs = [];
  let match = regex.exec(formula);
  while (match !== null) {
    refs.push(`[${match[1]}]`);
    match = regex.exec(formula);
  }
  return refs;
}

/**
 * Validates a formula string.
 * allowedFields should be in bracket format: ["[Document.SubTotal]", ...]
 * Returns { valid: boolean, error?: string }
 */
export function validateFormula(formula, allowedFields) {
  if (!formula || !formula.trim()) {
    return { valid: false, error: "Formula cannot be empty" };
  }

  try {
    const tokens = tokenize(formula);

    if (tokens.length === 0) {
      return { valid: false, error: "Formula cannot be empty" };
    }

    // Check all field references are in allowed list
    const refs = extractFieldReferences(formula);
    if (refs.length === 0) {
      // Must have at least one field reference
      return { valid: false, error: "Formula must reference at least one merge field" };
    }

    for (const ref of refs) {
      if (!allowedFields.includes(ref)) {
        return { valid: false, error: `Unknown field: ${ref}` };
      }
    }

    // Try parsing to check syntax
    parse(tokens);

    return { valid: true };
  } catch (err) {
    return { valid: false, error: err.message };
  }
}
